import { type Dayjs } from 'dayjs'
import { DateTimeRange } from '../model/DateTimeRange'
import type { DateTimeRangePickerState } from '../composables/DateTimeRangePickerState'
import { type Emits } from '../types'
import { applyTimeToDate } from '../utils'

export class DateTimeRangeSelectionService {
  constructor(
    private readonly navigation: DateTimeRangePickerState,
    private readonly emits: Emits,
    private readonly defaultTimeFrom?: string,
    private readonly defaultTimeTo?: string,
  ) {}

  handleDateSelection(clickedDate: Dayjs, range: DateTimeRange): void {
    if (this.navigation.isSelectingFrom()) {
      return this.handleFromSelection(clickedDate, range)
    }

    if (this.navigation.isSelectingTo()) {
      return this.handleToSelection(clickedDate, range)
    }

    this.handleDefaultSelection(clickedDate, range)
  }

  private handleFromSelection(clickedDate: Dayjs, range: DateTimeRange): void {
    // If clicking on existing from date, clear the range
    if (range.from && clickedDate.isSame(range.from, 'date')) {
      range.clear()
      this.navigation.setSelectingTo()
      this.emits('selecting-to')
      return
    }

    // If new from date is after to date or the range would exceed the limit, clear to and set new from
    if (
      range.to &&
      (clickedDate.isAfter(range.to, 'date') || this.exceedsMaxRange(clickedDate, range.to))
    ) {
      range.clearTo()
    }

    range.from = applyTimeToDate(clickedDate, this.defaultTimeFrom, range.from)

    this.navigation.setSelectingTo()
    this.emits('selecting-to')
  }

  private exceedsMaxRange(from: Dayjs, to: Dayjs): boolean {
    const maxRangeDays = this.navigation.getMaxRangeDays()

    if (maxRangeDays === null) {
      return false
    }

    const lastAllowedDate = from.tz().add(maxRangeDays, 'day')

    return to.isAfter(lastAllowedDate, 'date')
  }

  private handleToSelection(clickedDate: Dayjs, range: DateTimeRange): void {
    // If clicking on existing to date, clear the range
    if (range.to && clickedDate.isSame(range.to, 'date')) {
      range.clear()

      this.navigation.setSelectingFrom()
      this.emits('selecting-from')

      return
    }

    // If new to date is before from date, start a new selection from this date
    if (range.from && clickedDate.isBefore(range.from, 'date')) {
      range.from = applyTimeToDate(clickedDate, this.defaultTimeFrom, range.from)
      range.clearTo()

      this.navigation.setSelectingTo()
      this.emits('selecting-to')

      return
    }

    range.to = applyTimeToDate(clickedDate, this.defaultTimeTo, range.to)

    this.navigation.setSelectingFrom()
    this.emits('selecting-from')
  }

  private handleDefaultSelection(clickedDate: Dayjs, range: DateTimeRange): void {
    // Start by selecting from
    this.navigation.setSelectingFrom()

    // Check if the clicked date matches either selected date
    const isClickedDateSelected =
      (range.from && clickedDate.isSame(range.from, 'date')) ||
      (range.to && clickedDate.isSame(range.to, 'date'))

    // Scenario 1: Clear selection when clicking on the already selected date (when range is complete)
    if (range.from && range.to && isClickedDateSelected) {
      range.clear()

      return
    }

    // Scenario 2: No dates selected - start new selection
    if (!range.from && !range.to) {
      range.from = applyTimeToDate(clickedDate, this.defaultTimeFrom)
      this.navigation.setSelectingTo()
      this.emits('selecting-to')

      return
    }

    // Scenario 3: Only start date selected - complete or adjust the range
    if (range.from && !range.to) {
      if (clickedDate.isSame(range.from, 'date')) {
        // Same date clicked - set as end date
        range.to = applyTimeToDate(clickedDate, this.defaultTimeTo)

        this.navigation.setSelectingFrom()
        this.emits('selecting-from')
      } else if (clickedDate.isAfter(range.from, 'date')) {
        // Later date clicked - set as end date
        range.to = applyTimeToDate(clickedDate, this.defaultTimeTo)

        this.navigation.setSelectingFrom()
        this.emits('selecting-from')
      } else {
        // Earlier date clicked - replace start date
        range.from = applyTimeToDate(clickedDate, this.defaultTimeFrom)

        this.navigation.setSelectingTo()
        this.emits('selecting-to')
      }

      return
    }

    // Scenario 4: Both dates selected - start new selection
    if (range.from && range.to) {
      range.clearTo()
      range.from = applyTimeToDate(clickedDate, this.defaultTimeFrom, range.from)

      this.navigation.setSelectingTo()
      this.emits('selecting-to')
    }
  }
}
